import { useEffect } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { LDClient, useLDClient } from 'launchdarkly-react-client-sdk';
import { z } from 'zod';

import Constants from '@/constants';

import { OktaUserRaw } from '@/types';

import { useFeaturePermission } from './permissions';

const LD_FLAGS_EXCLUDED_FROM_RUM = new Set(['datadog-config', 'datadogConfig']);

/** RUM global context: boolean `allFlags()` entries plus fixed Pathway fields. */
const buildRumGlobalContextFromLd = (
  ldClient: LDClient,
): Record<string, string | boolean> => {
  const fromLd: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(
    ldClient.allFlags() as Record<string, unknown>,
  )) {
    if (typeof v === 'boolean' && !LD_FLAGS_EXCLUDED_FROM_RUM.has(k)) {
      fromLd[k] = v;
    }
  }
  return {
    ...fromLd,
    system_tag: 'pathway',
    team: 'OTS - Pathway 2.0',
    sub_dept: 'OTS',
    org_id: '221067',
  };
};

const setupDatadogRum = (
  user: OktaUserRaw,
  config: DatadogLDConfig,
  rumGlobalContext: Record<string, string | boolean>,
) => {
  const userId = user['cfa-guid'];
  const userCountry = user['cfa-loc-iso_country'];
  const userAudience = user.cfa_aud;
  const userLocations = user['cfa-locations'];
  const userPermissions = Object.keys(user.cfa_perms.P20);

  const shouldStartDatadog =
    !Constants.IS_LOCAL_DEPLOYMENT &&
    !Constants.IS_IN_CYPRESS_TEST &&
    Constants.DATADOG_APP_ID !== undefined &&
    Constants.DATADOG_CLIENT_TOKEN !== undefined;

  if (!shouldStartDatadog) {
    console.info(`Not starting DD integration...`);
    return;
  }

  const applicationId = Constants.DATADOG_APP_ID;
  const clientToken = Constants.DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    return;
  }

  datadogRum.init({
    applicationId,
    beforeSend: (_event, context) => {
      if ('isAborted' in context && context.isAborted) {
        return false;
      }
      return true;
    },
    clientToken,
    site: 'datadoghq.com',
    service: Constants.DATADOG_SERVICE,
    env: Constants.ENV,
    version: Constants.APP_VERSION,
    sessionSampleRate: config.sessionSampleRate,
    sessionReplaySampleRate: config.sessionReplaySampleRate,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'allow',
    allowedTracingUrls: config.allowedTracingUrls,
    traceSampleRate: config.tracingSamplingRate,
    compressIntakeRequests: true,
  });

  datadogLogs.init({
    beforeSend: (_event, context) => {
      if ('isAborted' in context && context.isAborted) {
        return false;
      }
      return true;
    },
    clientToken,
    site: 'datadoghq.com',
    service: Constants.DATADOG_SERVICE,
    env: Constants.ENV,
    version: Constants.APP_VERSION,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['log', 'info', 'warn', 'error'], //"all" or an Array of "log" "debug" "info" "warn" "error"
    sessionSampleRate: config.sessionSampleRate,
    // beforeSend: (log) => { // Discard specific logs. https://docs.datadoghq.com/logs/log_collection/javascript/#discard-specific-logs
    //   // discard 404 network errors
    //   if (log.http && log.http.status_code === 404) {
    //     return false
    //   }
    //   return true
    // },
  });

  datadogRum.setUser({
    id: userId,
    audience: userAudience,
    country: userCountry,
    locations: userLocations,
    permissions: userPermissions,
  });

  datadogLogs.setGlobalContext({
    service: Constants.DATADOG_SERVICE,
    userId: userId,
    userAudience: userAudience,
    userCountry: userCountry,
    userLocations: userLocations,
    userPermissions,
    system_tag: 'pathway',
    team: 'OTS - Pathway 2.0',
    sub_dept: 'OTS',
    org_id: '221067',
  });

  datadogRum.setGlobalContext(rumGlobalContext);
};

let datadogSetupAttempted = false;

export const DatadogRumSetup: React.FC<{ user?: OktaUserRaw }> = ({ user }) => {
  const rumEnabled = useFeaturePermission('rum');
  const client = useLDClient();

  useEffect(() => {
    if (!user || !rumEnabled || !client || datadogSetupAttempted) {
      return;
    }
    const ldConfig = getDatadogLDConfig(client);
    if (!ldConfig) {
      return;
    }
    datadogSetupAttempted = true;
    const rumGlobalContext = buildRumGlobalContextFromLd(client);
    if (Constants.IS_LOCAL_DEPLOYMENT) {
      console.info('[Datadog RUM] setGlobalContext payload:', rumGlobalContext);
    }
    setupDatadogRum(user, ldConfig, rumGlobalContext);
  }, [client, rumEnabled, user]);

  return null;
};

const configSchema = z.object({
  allowedTracingUrls: z.array(
    z.object({
      match: z.string(),
      propagatorTypes: z.array(
        z.union([
          z.literal('datadog'),
          z.literal('b3'),
          z.literal('b3multi'),
          z.literal('tracecontext'),
        ]),
      ),
    }),
  ),
  sessionReplaySampleRate: z.number().min(0).max(100),
  sessionSampleRate: z.number().min(0).max(100),
  tracingSamplingRate: z.number().min(0).max(100),
  web: z.object({
    sessionReplaySampleRate: z.number().min(0).max(100),
    sessionSampleRate: z.number().min(0).max(100),
    tracingSamplingRate: z.number().min(0).max(100),
  }),
});

type DatadogLDConfig = z.infer<typeof configSchema>;

const getDatadogLDConfig = (ld: LDClient) => {
  const flags = ld.allFlags();
  const configRaw = flags['datadog-config'] as DatadogLDConfig | undefined;

  const configParseAttempt = configSchema.safeParse({
    ...configRaw,
    ...configRaw?.web,
  });

  if (!configParseAttempt.success) {
    datadogRum.addError(
      new Error(
        `LaunchDarkly Datadog config parsing failed: ${configParseAttempt.error}`,
      ),
    );
    return undefined;
  }

  return configParseAttempt.data;
};
